import React, { useCallback, useEffect } from 'react';
import { Button, Input, message } from 'antd';
import { Sigma, Sparkles } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import { useRightPanelContext } from '../RightPanelContext';
import { PANEL_MODE } from '../../constants';
import { extensions } from '../../../extensions';
import { post } from '../../../../../axios/axios';
import { ResultCard } from '../shared/ResultCard';
import { PreviewActions } from '../shared/PreviewActions';
import styles from '../../../styles/index.module.scss';

const { TextArea } = Input;

/** 去掉 $$…$$、\[…\]、$…$、\(…\) 包裹，返回 { latex, type } */
const parseFormulaText = (formulaText, fallbackType = 'block') => {
    let latex = (formulaText || '').trim();
    let type = fallbackType;
    if (latex.startsWith('$$') && latex.endsWith('$$')) {
        latex = latex.slice(2, -2).trim();
        type = 'block';
    } else if (latex.startsWith('\\[') && latex.endsWith('\\]')) {
        latex = latex.slice(2, -2).trim();
        type = 'block';
    } else if (latex.startsWith('$') && latex.endsWith('$')) {
        latex = latex.slice(1, -1).trim();
        type = 'inline';
    } else if (latex.startsWith('\\(') && latex.endsWith('\\)')) {
        latex = latex.slice(2, -2).trim();
        type = 'inline';
    }
    return { latex, type };
};

const formulaToDoc = (formulaText) => {
    if (!formulaText) return null;
    const { latex } = parseFormulaText(formulaText);
    return {
        type: 'doc',
        content: [{ type: 'blockMath', attrs: { latex, blockId: null, detailId: null } }],
    };
};

const isCanceled = (error) =>
    error?.name === 'AbortError' ||
    error?.name === 'CanceledError' ||
    error?.code === 'ERR_CANCELED' ||
    error?.message?.includes('canceled');

/**
 * 公式修改面板（块级公式）
 */
const FormulaPanel = () => {
    const {
        editor,
        commonState,
        textState,
        formulaState,
        findFormulaNodeByBlockId,
        handleCancelPreview,
        handleCopyResult,
        handleReset,
    } = useRightPanelContext();

    const currentEditor = useEditor({ extensions, content: null, editable: false });
    const previewEditor = useEditor({ extensions, content: null, editable: false });

    useEffect(() => {
        if (!currentEditor) return;
        currentEditor.commands.setContent(formulaToDoc(formulaState.formulaInfo?.formulaText));
    }, [currentEditor, formulaState.formulaInfo]);

    useEffect(() => {
        if (!previewEditor) return;
        previewEditor.commands.setContent(formulaToDoc(formulaState.previewFormulaText));
    }, [previewEditor, formulaState.previewFormulaText]);

    const handleGenerate = useCallback(async () => {
        if (!editor || commonState.generating) return;
        if (commonState.showPreview) {
            commonState.showPreview = false;
            formulaState.previewFormulaText = '';
            formulaState.previewOriginalFormulaText = '';
            commonState.previewMode = '';
        }
        if (!formulaState.formulaInfo) {
            message.warning('请先选中公式');
            return;
        }
        const { formulaText, blockId } = formulaState.formulaInfo;

        commonState.generating = true;
        commonState.generationProgress = 20;
        commonState.generationStatus = '正在重写公式…';
        try {
            const response = await post('/ai/ai/paper/editor/v2/rewrite-latex', {
                selectedContent: formulaText,
                userRequirement: textState.customInstruction.trim() || '重写此公式',
                contentBlockId: blockId,
            });
            commonState.generationProgress = 100;
            commonState.generationStatus = '';
            commonState.generating = false;

            const resultContent = response?.data;
            if (resultContent) {
                formulaState.previewOriginalFormulaText = formulaText;
                formulaState.previewFormulaText = resultContent;
                commonState.showPreview = true;
                commonState.previewMode = 'modify-formula';
            } else {
                message.warning('接口返回数据格式不正确');
            }
        } catch (error) {
            if (isCanceled(error)) return;
            commonState.generating = false;
            commonState.generationStatus = '公式重写失败，请重试';
            console.error('公式重写接口调用失败:', error);
            message.error(error?.response?.data?.msg || '公式重写失败，请重试');
        }
    }, [editor, commonState, textState, formulaState]);

    const handleConfirmApply = useCallback(async () => {
        if (!editor || !formulaState.formulaInfo) return;
        const { blockId, type: formulaType } = formulaState.formulaInfo;
        const newFormulaText = formulaState.previewFormulaText;
        if (!newFormulaText) {
            message.warning('预览内容为空');
            return;
        }
        try {
            const { latex: newLatex, type: newType } = parseFormulaText(
                newFormulaText,
                formulaType,
            );
            const info = findFormulaNodeByBlockId(editor, blockId);
            if (!info) throw new Error('找不到原公式节点');
            const { node: oldNode, pos: oldPos } = info;

            const newNode = {
                type: newType === 'block' ? 'blockMath' : 'inlineMath',
                attrs: { latex: newLatex, blockId, detailId: oldNode.attrs.detailId },
            };
            const { tr } = editor.state;
            tr.setMeta('allowFormulaModification', true);
            tr.replaceWith(oldPos, oldPos + oldNode.nodeSize, editor.schema.nodeFromJSON(newNode));
            editor.view.dispatch(tr);
            editor.commands.focus();

            const editedContent = newType === 'block' ? `$$${newLatex}$$` : `$${newLatex}$`;
            try {
                await post('/ai/ai/paper/editor/v2/save', { contentBlockId: blockId, editedContent });
            } catch (saveError) {
                console.error('保存公式内容失败:', saveError);
            }

            message.success('公式已更新');

            commonState.showPreview = false;
            formulaState.previewFormulaText = '';
            formulaState.previewOriginalFormulaText = '';
            formulaState.formulaInfo = null;
            formulaState.isFormulaSelected = false;
            commonState.previewMode = '';
            commonState.mode = PANEL_MODE.EMPTY;
            textState.selectedText = '';
            textState.selectedBlockIds = [];
            textState.customInstruction = '';
            textState.currentAction = '';
            textState.selectedRange = { from: null, to: null };
        } catch (error) {
            console.error('替换公式节点失败:', error);
            message.error(error.message || '替换公式节点失败');
        }
    }, [commonState, textState, formulaState, editor, findFormulaNodeByBlockId]);

    if (commonState.showPreview && formulaState.previewFormulaText) {
        return (
            <div className={styles.panelSection}>
                <ResultCard
                    title="公式重写结果"
                    meta="公式"
                    actions={
                        <PreviewActions
                            onCancel={handleCancelPreview}
                            onCopy={handleCopyResult}
                            onRegenerate={handleGenerate}
                            onApply={handleConfirmApply}
                            generating={commonState.generating}
                            applyText="替换公式"
                        />
                    }
                >
                    <div className={styles.compareLabel}>原公式</div>
                    <div className={`${styles.previewDoc} ${styles.previewDocOriginal}`}>
                        <EditorContent editor={currentEditor} />
                    </div>
                    <div className={styles.compareLabel}>重写后</div>
                    <div className={`${styles.previewDoc} ${styles.previewDocNew}`}>
                        <EditorContent editor={previewEditor} />
                    </div>
                </ResultCard>
            </div>
        );
    }

    return (
        <div className={styles.panelSection}>
            <div className={styles.quoteCard}>
                <div className={styles.quoteCardLabel}>
                    <Sigma size={14} />
                    已选中公式
                </div>
                <div className={styles.quoteCardFormula}>
                    <EditorContent editor={currentEditor} />
                </div>
            </div>
            <div className={styles.presetDesc}>
                可以修改公式的数学表达式、变量符号或书写形式。
            </div>

            <div className={styles.sectionLabel}>修改要求（可选）</div>
            <TextArea
                value={textState.customInstruction}
                onChange={(e) => {
                    textState.customInstruction = e.target.value;
                }}
                placeholder="例如：把变量名改成英文、化简表达式…"
                autoSize={{ minRows: 3, maxRows: 6 }}
                disabled={commonState.generating}
                className={styles.instructionInput}
            />

            {commonState.generationStatus && (
                <div className={styles.generationStatus}>{commonState.generationStatus}</div>
            )}

            <Button
                type="primary"
                block
                size="large"
                icon={<Sparkles size={16} />}
                onClick={handleGenerate}
                loading={commonState.generating}
                className={styles.primaryAction}
            >
                {commonState.generating ? 'AI 处理中…' : '开始重写公式'}
            </Button>
            <Button block type="text" onClick={handleReset} disabled={commonState.generating}>
                取消
            </Button>
        </div>
    );
};

export default FormulaPanel;
